import { StateCreator } from "zustand";
import { VendState, TaskFiltersState } from ".";
import { OperatorUser, Task, TaskStatus } from "../types";
import {
  bulkCancelTasks as bulkCancelTasksApi,
  bulkCreateTasks as bulkCreateTasksApi,
  bulkDeleteTasks as bulkDeleteTasksApi,
  bulkEditTasks as bulkEditTasksApi,
  backfillCollectionTasks as backfillCollectionTasksApi,
  completeRefillInventoryTask as completeRefillInventoryTaskApi,
  createTask as createTaskApi,
  createRefillInventoryPickup,
  deleteTask as deleteTaskApi,
  deleteTaskAsset as deleteTaskAssetApi,
  editTask as editTaskApi,
  fetchTaskById,
  fetchTasks,
  fetchContactTasks,
  getTasksCacheVersion,
  invalidateTasksCache,
  setRefillInventorySource as setRefillInventorySourceApi,
  fetchOperators,
  taskResponseIncludesVisitMetrics,
  updateTaskStatuses as updateTaskStatusesApi,
} from "../api/api";
import type {
  CollectionBackfillInput,
  RefillInventoryCompletionLineInput,
  TaskListFilters,
} from "../api/api";
import { withWriteAction } from "../utils";
import { runLoad } from "./helpers";
import { getEffectiveVendViewMode } from "../effectiveUser";
import { getTaskIdentityKey } from "../taskContext";
import {
  getConnectedServiceBundleTasks,
} from "../taskBundle";
import { getTaskDayKey } from "../components/taskDateHelpers";
import { taskScopeKeys, usesRoutableTaskFeed } from "../taskScopes";
import { suppressRealtimeEvent } from "../realtime/suppressRealtime";
import { isRouteTask, isTaskFinalStatus } from "../taskHelpers";
import { measureTaskStep, measureTaskSync } from "../taskPerf";
import { removeLastRoutesMatching } from "../navigation/sectionHistory";
import { useNavHistory } from "./navHistory";
import { mergeTaskSnapshot } from "./taskMerge";
import {
  taskCacheFiltersForTaskChanges,
  taskScheduleRangesForTasks,
} from "../taskCacheFilters";
import { resetMachineCollectionSummaryCache } from "../liveSales/store";

const TASK_REALTIME_SUPPRESS_MS = 10_000;
const TASK_CREATE_REALTIME_SUPPRESS_MS = 10_000;
type TaskLoadInFlightEntry = {
  promise: Promise<void>;
  scopeKeys: Set<string>;
};
const taskLoadInFlight = new Map<string, TaskLoadInFlightEntry>();

export const clearTaskLoadInFlight = () => {
  taskLoadInFlight.clear();
};

const isTaskDetailPathForIds = (path: string, taskIds: Set<string>) =>
  Array.from(taskIds).some(
    (taskId) =>
      path.startsWith(`/tasks/${taskId}`) ||
      path.startsWith(`/contact/tasks/${taskId}`)
  );

const clearDeletedTaskRoutes = (taskIds: string[]) => {
  const deletedIds = new Set(taskIds.filter(Boolean));
  if (!deletedIds.size) return;
  removeLastRoutesMatching((path) => isTaskDetailPathForIds(path, deletedIds));
  useNavHistory
    .getState()
    .removeMatching((path) => isTaskDetailPathForIds(path, deletedIds));
};

const suppressTaskRealtime = (
  taskIds: Array<string | null | undefined>
) => {
  Array.from(
    new Set(taskIds.filter((taskId): taskId is string => Boolean(taskId)))
  ).forEach((taskId) =>
    suppressRealtimeEvent("task.changed", taskId, TASK_REALTIME_SUPPRESS_MS)
  );
};

const suppressCreatedTaskRealtime = () => {
  suppressRealtimeEvent("task.changed", TASK_CREATE_REALTIME_SUPPRESS_MS);
};

const actionLabelForUpdate = (
  updates: Partial<Task>,
  bundleCount = 0
) => {
  const plural = bundleCount > 0 ? "Tasks" : "Task";
  if (updates.scheduledFor !== undefined) return `Rescheduling ${plural}`;
  if ((updates as any).userId !== undefined) return `Reassigning ${plural}`;
  if (updates.status === "cancelled") return `Cancelling ${plural}`;
  if (updates.status === "done") return `Completing ${plural}`;
  if (updates.notes !== undefined) return "Updating Notes";
  return `Updating ${plural}`;
};

const actionLabelForStatus = (status: TaskStatus, count: number) => {
  const plural = count === 1 ? "Task" : "Tasks";
  if (status === "done") return `Completing ${plural}`;
  if (status === "cancelled") return `Cancelling ${plural}`;
  return `Updating ${plural}`;
};

const isContactView = () => getEffectiveVendViewMode() === "contact";

const mergeTasksById = (
  existingTasks: Task[],
  updatedTasks: Task[],
  options?: { preserveNonEmptyPickupLines?: boolean }
) => {
  const byId = new Map(existingTasks.map((task) => [task.id, task]));
  updatedTasks.forEach((task) => {
    byId.set(task.id, mergeTaskSnapshot(byId.get(task.id), task, options));
  });
  return Array.from(byId.values());
};

const completionTimestampForStatus = (status: TaskStatus) =>
  status === "done" || status === "cancelled"
    ? new Date().toISOString()
    : null;

const applyStatusLocally = (
  task: Task,
  status: TaskStatus,
  completedAt: string | null
): Task => ({
  ...task,
  status,
  doneAt: completedAt ?? undefined,
  startedAt:
    status === "done" && completedAt
      ? task.startedAt || completedAt
      : task.startedAt,
  isLive: isTaskFinalStatus(status) ? false : task.isLive,
});

const isNotesOnlyUpdate = (updates: Record<string, unknown>) => {
  const keys = Object.keys(updates).filter(
    (key) => updates[key] !== undefined
  );
  return keys.length === 1 && keys[0] === "notes";
};

const affectedScheduleRangesForTasks = (
  previousTasks: Array<Task | null | undefined>,
  nextTasks: Array<Task | null | undefined>
) => taskScheduleRangesForTasks([...previousTasks, ...nextTasks]);

const taskDateChanged = (
  previousTasks: Array<Task | null | undefined>,
  nextTasks: Array<Task | null | undefined>
) => {
  const previousById = new Map(
    previousTasks
      .filter((task): task is Task => Boolean(task))
      .map((task) => [task.id, getTaskDayKey(task.scheduledFor || task.createdAt)])
  );
  const nextById = new Map(
    nextTasks
      .filter((task): task is Task => Boolean(task))
      .map((task) => [task.id, getTaskDayKey(task.scheduledFor || task.createdAt)])
  );
  const taskIds = new Set(
    Array.from(previousById.keys()).concat(Array.from(nextById.keys()))
  );
  return Array.from(taskIds).some(
    (taskId) => previousById.get(taskId) !== nextById.get(taskId)
  );
};

const affectedTaskScopeKeys = (
  previousTasks: Array<Task | null | undefined>,
  nextTasks: Array<Task | null | undefined>
) => {
  const scopeKeys = new Set<string>();
  affectedScheduleRangesForTasks(previousTasks, nextTasks).forEach((range) => {
    scopeKeys.add(taskScopeKeys.dateRange(range.scheduledFrom, range.scheduledTo));
    scopeKeys.add(
      taskScopeKeys.dateRange(range.scheduledFrom, range.scheduledTo, {
        includeVisitMetrics: false,
      })
    );
  });
  [...previousTasks, ...nextTasks].forEach((task) => {
    if (task?.machineId) scopeKeys.add(taskScopeKeys.machine(task.machineId));
    if (task?.locationId) scopeKeys.add(taskScopeKeys.location(task.locationId));
  });
  return Array.from(scopeKeys);
};

const taskMetadataSignature = (task: Task | null | undefined) =>
  task
    ? [
        task.id,
        task.type,
        task.status,
        task.scheduledFor || "",
        task.machineId || "",
        task.locationId || "",
      ].join("|")
    : "";

const taskMetadataSignatures = (tasks: Array<Task | null | undefined>) =>
  tasks.map(taskMetadataSignature).filter(Boolean).sort().join("\n");

const affectsMachineVisitMetadata = (
  previousTasks: Array<Task | null | undefined>,
  nextTasks: Array<Task | null | undefined>
) => taskMetadataSignatures(previousTasks) !== taskMetadataSignatures(nextTasks);

const collectionSummaryMachineIdsForTasks = (
  tasks: Array<Task | null | undefined>
) =>
  Array.from(
    new Set(
      tasks
        .filter(
          (task): task is Task =>
            Boolean(task?.machineId) && task?.type === "machine_collection"
        )
        .map((task) => task.machineId!)
    )
  );

export type TaskSlice = {
  tasks: Task[];
  tasksHydrated: boolean;
  tasksLoading: boolean;
  tasksError: string | null;
  tasksFetchedAt: number | null;
  taskContextKey: string | null;
  taskScopes: Record<string, { loadedAt: number }>;
  operators: OperatorUser[];
  operatorsHydrated: boolean;
  operatorsLoading: boolean;
  operatorsError: string | null;
  taskFilters: TaskFiltersState;
  taskFiltersByKey: Record<string, TaskFiltersState>;
  setTasks: (tasks: Task[]) => void;
  syncTaskContext: () => void;
  mergeTaskSnapshots: (
    tasks: Task[],
    options?: {
      replaceTaskIds?: string[];
      preserveNonEmptyPickupLines?: boolean;
    }
  ) => void;
  removeTaskSnapshots: (taskIds: string[]) => void;
  setTaskFilters: (updates: Partial<TaskFiltersState>) => void;
  setTaskFiltersForKey: (key: string, updates: Partial<TaskFiltersState>) => void;
  loadTasks: (options?: {
    force?: boolean;
    silent?: boolean;
    filters?: TaskListFilters;
    scopeKey?: string;
    scopeKeys?: string[];
  }) => Promise<void>;
  loadOperators: () => Promise<void>;
  loadTask: (taskId: string) => Promise<Task>;
  createTask: (payload: {
    type: Task["type"];
    machineId: string;
    locationId?: string;
    notes?: string;
    scheduledFor?: string;
    assigneeId?: string | null;
    childTaskTypes?: Task["type"][];
  }, options?: { skipRefresh?: boolean }) => Promise<Task>;
  createTasks: (
    payloads: Array<{
      type: Task["type"];
      machineId: string;
      locationId?: string;
      notes?: string;
      scheduledFor?: string;
      assigneeId?: string | null;
      childTaskTypes?: Task["type"][];
    }>,
    options?: { skipRefresh?: boolean }
  ) => Promise<Task[]>;
  bulkUpdateTasks: (
    taskIds: string[],
    updates: Partial<
      Pick<
        Task,
        | "notes"
        | "scheduledFor"
        | "userId"
        | "grossCents"
        | "refundsCents"
        | "commissionCents"
        | "netCents"
        | "status"
      >
    > & {
      includeRefundsInCommission?: boolean | null;
      prefill?: boolean | null;
    },
    options?: {
      skipRefresh?: boolean;
      suppressOverlay?: boolean;
    }
  ) => Promise<Task[]>;
  updateTask: (
    taskId: string,
    updates: Partial<
      Pick<
        Task,
        "notes"
        | "status"
        | "type"
        | "machineId"
        | "locationId"
        | "scheduledFor"
        | "userId"
        | "grossCents"
        | "refundsCents"
        | "commissionCents"
        | "netCents"
      >
    > & { includeRefundsInCommission?: boolean | null },
    options?: {
      skipRefresh?: boolean;
      skipStatusUpdate?: boolean;
      suppressOverlay?: boolean;
    }
  ) => Promise<Task>;
  markTaskComplete: (taskId: string) => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;
  deleteTasks: (taskIds: string[]) => Promise<void>;
  cancelTasks: (
    taskIds: string[],
    options?: { skipRefresh?: boolean }
  ) => Promise<Task[]>;
  setTaskRefillInventorySource: (
    taskId: string,
    warehouseId: string | null,
    sourceMode?: Task["inventorySourceMode"],
    vanId?: string | null
  ) => Promise<Task>;
  createTaskRefillInventoryPickup: (taskId: string, warehouseId: string, vanId: string) => Promise<Task>;
  completeTaskRefillInventory: (
    taskId: string,
    items: RefillInventoryCompletionLineInput[]
  ) => Promise<Task>;
  deleteTaskAsset: (assetId: string, taskId?: string | null) => Promise<void>;
  backfillCollectionTasks: (payload: {
    locationId: string;
    items: CollectionBackfillInput[];
  }) => Promise<Task[]>;
  updateTasksNotes: (
    taskIds: string[],
    notes: string,
    options?: {
      skipRefresh?: boolean;
      suppressOverlay?: boolean;
    }
  ) => Promise<void>;
  updateTasksStatus: (
    taskIds: string[],
    status: TaskStatus,
    options?: { skipRefresh?: boolean; cascadeBundle?: boolean }
  ) => Promise<void>;
};

export const createTaskSlice: StateCreator<VendState, [], [], TaskSlice> = (
  set,
  get
) => {
  const refreshAffectedTaskRanges = async (
    previousTasks: Array<Task | null | undefined>,
    nextTasks: Array<Task | null | undefined>
  ) => {
    if (!taskDateChanged(previousTasks, nextTasks)) return;
    const ranges = affectedScheduleRangesForTasks(previousTasks, nextTasks);
    await measureTaskStep(
      `refresh affected task ranges (${ranges.length})`,
      () =>
        Promise.all(
          ranges.map(({ scheduledFrom, scheduledTo }) =>
            (get() as VendState).loadTasks({
              force: true,
              silent: true,
              filters: { scheduledFrom, scheduledTo },
              scopeKey: taskScopeKeys.dateRange(scheduledFrom, scheduledTo),
            })
          )
        ).then(() => undefined)
    );
  };

  const taskLoadLabel = (filters?: TaskListFilters, scopeKeys?: string[]) => {
    const scopes = scopeKeys?.length ? scopeKeys.join(",") : "all";
    const parts = [
      filters?.machineId ? `machine:${filters.machineId}` : "",
      filters?.locationId ? `location:${filters.locationId}` : "",
      filters?.scheduledFrom && filters?.scheduledTo
        ? `date:${filters.scheduledFrom}..${filters.scheduledTo}`
        : "",
      filters?.includeVisitMetrics === false ? "no-visit-metrics" : "",
      filters?.responseMode === "report" ? "report" : "",
      filters?.overdueTo ? `overdue:${filters.overdueTo}` : "",
    ].filter(Boolean);
    return parts.length ? parts.join(" ") : scopes;
  };
  const taskLoadKey = (
    contactView: boolean,
    taskContextKey: string,
    filters?: TaskListFilters,
    _scopeKeys?: string[]
  ) =>
    JSON.stringify({
      contactView,
      taskContextKey,
      filters: filters || null,
    });

  const ensureTaskContext = () => {
    const taskContextKey = getTaskIdentityKey();
    const current = get() as VendState;
    if (current.taskContextKey === taskContextKey) return taskContextKey;
    clearTaskLoadInFlight();
    set((state) => ({
      ...(state as VendState),
      tasks: [],
      tasksHydrated: false,
      tasksLoading: false,
      tasksError: null,
      tasksFetchedAt: null,
      taskScopes: {},
      taskContextKey,
    }));
    return taskContextKey;
  };

  const invalidateAffectedTaskScopes = (
    previousTasks: Array<Task | null | undefined>,
    nextTasks: Array<Task | null | undefined>,
    options?: { keepDateScopes?: boolean }
  ) => {
    const scopeKeys = affectedTaskScopeKeys(previousTasks, nextTasks).filter(
      (scopeKey) => !(options?.keepDateScopes && scopeKey.startsWith("date:"))
    );
    if (!scopeKeys.length) return;
    set((state) => {
      const vendState = state as VendState;
      const taskScopes = { ...(vendState.taskScopes || {}) };
      scopeKeys.forEach((scopeKey) => {
        delete taskScopes[scopeKey];
      });
      return {
        ...vendState,
        taskScopes,
      };
    });
  };

  const refreshMachineMetadataIfNeeded = async (
    previousTasks: Array<Task | null | undefined>,
    nextTasks: Array<Task | null | undefined>
  ) => {
    if (
      affectsMachineVisitMetadata(previousTasks, nextTasks) &&
      (get() as VendState).machinesHydrated
    ) {
      await (get() as VendState).loadMachines({ force: true });
    }
  };

  const reconcileCompletedServiceParents = async (
    previousTasks: Array<Task | null | undefined>,
    nextTasks: Array<Task | null | undefined>
  ): Promise<Task[]> => {
    const previousById = new Map(
      previousTasks.filter((task): task is Task => Boolean(task)).map((task) => [task.id, task])
    );
    const newlyFinalChildren = nextTasks.filter((task): task is Task =>
      Boolean(
        task?.serviceTaskId &&
          isTaskFinalStatus(task.status) &&
          !isTaskFinalStatus(previousById.get(task.id)?.status)
      )
    );
    if (!newlyFinalChildren.length) return [];

    const tasks = (get() as VendState).tasks || [];
    const serviceIds = new Set(newlyFinalChildren.map((task) => task.serviceTaskId as string));
    const readyParents = tasks.filter((task) => {
      if (task.type !== "machine_service" || !serviceIds.has(task.id)) return false;
      if (isTaskFinalStatus(task.status)) return false;
      const children = tasks.filter((candidate) => candidate.serviceTaskId === task.id);
      return children.length > 0 && children.every((child) => isTaskFinalStatus(child.status));
    });
    if (!readyParents.length) return [];

    const parentIds = readyParents.map((task) => task.id);
    suppressTaskRealtime(parentIds);
    return updateTaskStatusesApi(parentIds, "done", { cascadeBundle: false });
  };

  const finalizeTaskMutation = async ({
    previousTasks,
    nextTasks,
    deletedTaskIds = [],
    skipRefresh,
    skipScopeInvalidation,
  }: {
    previousTasks: Array<Task | null | undefined>;
    nextTasks: Array<Task | null | undefined>;
    deletedTaskIds?: string[];
    skipRefresh?: boolean;
    skipScopeInvalidation?: boolean;
  }) => {
    if (deletedTaskIds.length) {
      clearDeletedTaskRoutes(deletedTaskIds);
    }
    invalidateTasksCache(
      taskCacheFiltersForTaskChanges(
        previousTasks,
        nextTasks,
        Object.keys(((get() as VendState).taskScopes || {}))
      )
    );
    resetMachineCollectionSummaryCache(
      collectionSummaryMachineIdsForTasks([...previousTasks, ...nextTasks])
    );
    const updatedTasks = nextTasks.filter((task): task is Task => Boolean(task));
    set((state) => {
      const vendState = state as VendState;
      const deletedIds = new Set(deletedTaskIds);
      const baseTasks = deletedIds.size
        ? (vendState.tasks || []).filter((task) => !deletedIds.has(task.id))
        : vendState.tasks || [];
      return {
        ...vendState,
        tasks: updatedTasks.length ? mergeTasksById(baseTasks, updatedTasks) : baseTasks,
        tasksFetchedAt: Date.now(),
      };
    });
    const reconciledParents = await reconcileCompletedServiceParents(
      previousTasks,
      nextTasks
    );
    if (reconciledParents.length) {
      updatedTasks.push(...reconciledParents);
      set((state) => {
        const vendState = state as VendState;
        return {
          ...vendState,
          tasks: mergeTasksById(vendState.tasks || [], reconciledParents),
          tasksFetchedAt: Date.now(),
        };
      });
    }
    if (!skipScopeInvalidation) {
      invalidateAffectedTaskScopes(previousTasks, nextTasks, {
        keepDateScopes: !skipRefresh,
      });
    }
    const refreshes: Promise<unknown>[] = [];
    if (!skipRefresh) {
      refreshes.push(refreshAffectedTaskRanges(previousTasks, nextTasks));
    }
    refreshes.push(refreshMachineMetadataIfNeeded(previousTasks, nextTasks));
    if (refreshes.length) {
      void Promise.all(refreshes).catch(() => undefined);
    }
  };

  return {
  tasks: [],
  tasksHydrated: false,
  tasksLoading: false,
  tasksError: null,
  tasksFetchedAt: null,
  taskContextKey: null,
  taskScopes: {},
  operators: [],
  operatorsHydrated: false,
  operatorsLoading: false,
  operatorsError: null,
  taskFilters: { statuses: ["unassigned", "pending"], type: "all", query: "" },
  taskFiltersByKey: {},
  syncTaskContext: () => {
    ensureTaskContext();
  },
  setTasks: (tasks) => set({ tasks, tasksFetchedAt: Date.now() } as VendState),
  mergeTaskSnapshots: (tasks, options) => {
    resetMachineCollectionSummaryCache(collectionSummaryMachineIdsForTasks(tasks));
    set((state) => {
      const vendState = state as VendState;
      const replaceTaskIds = new Set(options?.replaceTaskIds || []);
      const baseTasks = replaceTaskIds.size
        ? (vendState.tasks || []).filter((task) => !replaceTaskIds.has(task.id))
        : vendState.tasks || [];
      return {
        ...vendState,
        tasks: mergeTasksById(baseTasks, tasks, {
          preserveNonEmptyPickupLines: options?.preserveNonEmptyPickupLines,
        }),
        tasksFetchedAt: Date.now(),
      };
    });
  },
  removeTaskSnapshots: (taskIds) =>
    set((state) => {
      const vendState = state as VendState;
      const deletedTaskIds = new Set(taskIds.filter(Boolean));
      if (!deletedTaskIds.size) return vendState;
      resetMachineCollectionSummaryCache(
        collectionSummaryMachineIdsForTasks(
          (vendState.tasks || []).filter((task) => deletedTaskIds.has(task.id))
        )
      );
      clearDeletedTaskRoutes(Array.from(deletedTaskIds));
      return {
        ...vendState,
        tasks: (vendState.tasks || []).filter(
          (task) => !deletedTaskIds.has(task.id)
        ),
        tasksFetchedAt: Date.now(),
      };
    }),
  setTaskFilters: (updates) =>
    set((state) => ({
      ...(state as VendState),
      taskFilters: { ...(state as VendState).taskFilters, ...updates },
    })),
  setTaskFiltersForKey: (key, updates) =>
    set((state) => {
      const current = (state as VendState).taskFiltersByKey?.[key] || {
        statuses: ["unassigned", "pending"],
        type: "all",
        query: "",
      };
      return {
        ...(state as VendState),
        taskFiltersByKey: {
          ...(state as VendState).taskFiltersByKey,
          [key]: { ...current, ...updates },
        },
      };
    }),
  loadTasks: async (options) => {
    const taskContextKey = ensureTaskContext();
    const silent = options?.silent === true;
    const requestedFilters = options?.filters;
    const scopeKeys = Array.from(
      new Set([options?.scopeKey, ...(options?.scopeKeys || [])].filter(Boolean))
    ) as string[];
    const scoped = Boolean(options?.filters || scopeKeys.length);
    if (scoped) {
      if (
        !options?.force &&
        scopeKeys.length > 0 &&
        scopeKeys.every((scopeKey) => Boolean((get() as VendState).taskScopes?.[scopeKey]))
      ) {
        return;
      }
      const contactView = isContactView();
      const loadLabel = taskLoadLabel(requestedFilters, scopeKeys);
      const loadKey = taskLoadKey(
        contactView,
        taskContextKey,
        requestedFilters,
        scopeKeys
      );
      const existingLoad = taskLoadInFlight.get(loadKey);
      if (existingLoad) {
        scopeKeys.forEach((scopeKey) => existingLoad.scopeKeys.add(scopeKey));
        await existingLoad.promise;
        return;
      }
      let finishSharedLoad = () => {};
      const sharedLoad = new Promise<void>((resolve) => {
        finishSharedLoad = resolve;
      });
      const sharedEntry: TaskLoadInFlightEntry = {
        promise: sharedLoad,
        scopeKeys: new Set(scopeKeys),
      };
      taskLoadInFlight.set(loadKey, sharedEntry);
      if (!silent || scopeKeys.some((key) => !(get() as VendState).taskScopes?.[key])) {
        set((state) => ({
          ...(state as VendState),
          tasksLoading: silent ? (state as VendState).tasksLoading : true,
          tasksError: null,
        }));
      }
      try {
        let startVersion = getTasksCacheVersion({
          contact: contactView,
          filters: requestedFilters,
        });
        let data = await measureTaskStep(
          `load tasks API ${loadLabel}`,
          () =>
            contactView
              ? fetchContactTasks({
                  force: options?.force,
                  filters: requestedFilters,
                })
              : fetchTasks({
                  force: options?.force,
                  filters: requestedFilters,
                }),
          "/task/",
        );
        if (
          startVersion !==
          getTasksCacheVersion({ contact: contactView, filters: requestedFilters })
        ) {
          startVersion = getTasksCacheVersion({
            contact: contactView,
            filters: requestedFilters,
          });
          data = await measureTaskStep(
            `load tasks API retry ${loadLabel}`,
            () =>
              contactView
                ? fetchContactTasks({
                    force: true,
                    filters: requestedFilters,
                  })
                : fetchTasks({
                    force: true,
                    filters: requestedFilters,
                  }),
            "/task/",
          );
        }
        if (
          startVersion !==
          getTasksCacheVersion({ contact: contactView, filters: requestedFilters })
        ) {
          throw new Error("Tasks changed while loading. Please retry.");
        }
        if (getTaskIdentityKey() !== taskContextKey) return;
        measureTaskSync(`load tasks merge ${loadLabel}`, () => {
          set((state) => {
            const vendState = state as VendState;
            if (vendState.taskContextKey !== taskContextKey) return vendState;
            const range = requestedFilters?.scheduledFrom && requestedFilters?.scheduledTo
              ? {
                  scheduledFrom: requestedFilters.scheduledFrom,
                  scheduledTo: requestedFilters.scheduledTo,
                }
              : null;
            const overdueTo = requestedFilters?.overdueTo || null;
            const machineId = requestedFilters?.machineId || null;
            const locationId = requestedFilters?.locationId || null;
            const statuses = requestedFilters?.statuses?.length
              ? new Set(requestedFilters.statuses)
              : null;
            const currentTasks = vendState.tasks || [];
            const currentTasksById = new Map(currentTasks.map((task) => [task.id, task]));
            const includesVisitMetrics =
              taskResponseIncludesVisitMetrics(requestedFilters);
            const routableFeed = usesRoutableTaskFeed(requestedFilters);
            const mergedData = data.map((task) =>
              mergeTaskSnapshot(currentTasksById.get(task.id), task, {
                incomingPartial: requestedFilters?.responseMode === "report",
                includesVisitMetrics,
              })
            );
            const shouldPreserveContactHiddenLiveTask = (task: Task) =>
              contactView &&
              task.type === "machine_pickup_inventory" &&
              task.isLive &&
              Boolean(
                (machineId && task.machineId === machineId) ||
                  (locationId && task.locationId === locationId)
              );
            const baseTasks =
              range || overdueTo || machineId || locationId || statuses
                ? currentTasks.filter((task) => {
                    if (shouldPreserveContactHiddenLiveTask(task)) return true;
                    // Contact views and route feeds do not own remote lead rows.
                    // Organization reports include both field and remote leads.
                    if (task.type === "location_lead") {
                      if (contactView) return true;
                      if (routableFeed && !isRouteTask(task)) {
                        return true;
                      }
                    }
                    const dayKey = getTaskDayKey(task.scheduledFor || task.createdAt);
                    const inRange = Boolean(
                      range &&
                        dayKey &&
                        dayKey >= range.scheduledFrom &&
                        dayKey <= range.scheduledTo
                    );
                    const inOverdueSlice = Boolean(
                      overdueTo &&
                        dayKey &&
                        dayKey <= overdueTo &&
                        (task.status === "pending" || task.status === "unassigned")
                    );
                    const inMachineScope = Boolean(
                      machineId && task.machineId === machineId
                    );
                    const inLocationScope = Boolean(
                      locationId && task.locationId === locationId
                    );
                    const inStatusScope = Boolean(
                      statuses && statuses.has(task.status)
                    );
                    const matchesDateSlice =
                      range || overdueTo
                        ? inRange || inOverdueSlice
                        : true;
                    const matchesMachine =
                      !machineId || inMachineScope;
                    const matchesLocation =
                      !locationId || inLocationScope;
                    const matchesStatus =
                      !statuses || inStatusScope;
                    const matchesRequestedSlice =
                      matchesDateSlice &&
                      matchesMachine &&
                      matchesLocation &&
                      matchesStatus;
                    return !matchesRequestedSlice;
                  })
                : requestedFilters?.responseMode === "report"
                  ? currentTasks.filter(task => !task.type.startsWith("machine_") &&
                      (contactView || task.type !== "location_lead"))
                  : vendState.tasks || [];
            return {
              ...vendState,
              tasks: mergeTasksById(baseTasks, mergedData),
              tasksFetchedAt: Date.now(),
              taskScopes: sharedEntry.scopeKeys.size
                ? Array.from(sharedEntry.scopeKeys).reduce(
                    (scopes, scopeKey) => ({
                      ...scopes,
                      [scopeKey]: { loadedAt: Date.now() },
                    }),
                    { ...(vendState.taskScopes || {}) }
                  )
                : vendState.taskScopes,
              tasksLoading: silent ? vendState.tasksLoading : false,
            };
          });
        });
      } catch (error: any) {
        if (getTaskIdentityKey() !== taskContextKey) return;
        set((state) => ({
          ...(state as VendState),
          tasksError: silent && scopeKeys.every((key) => Boolean((state as VendState).taskScopes?.[key]))
            ? (state as VendState).tasksError
            : error?.message || "Failed to load tasks",
          tasksLoading: silent ? (state as VendState).tasksLoading : false,
        }));
      } finally {
        if (taskLoadInFlight.get(loadKey) === sharedEntry) {
          taskLoadInFlight.delete(loadKey);
        }
        finishSharedLoad();
      }
      return;
    }
    await runLoad({
      isLoading: () =>
        options?.force ? false : (get() as VendState).tasksLoading,
      setLoading: (loading) =>
        silent
          ? void 0
          : set((state) => ({
              ...(state as VendState),
              tasksLoading: loading,
            })),
      setError: (message) =>
        silent
          ? void 0
          : set((state) => ({
              ...(state as VendState),
              tasksError: message,
            })),
      setHydrated: (hydrated) =>
        set((state) => ({
          ...(state as VendState),
          tasksHydrated: hydrated,
        })),
      hydrateOnError: true,
      errorMessage: "Failed to load tasks",
      fetcher: async () => {
        const contactView = isContactView();
        const loadTaskContextKey = taskContextKey;
        let startVersion = getTasksCacheVersion({
          contact: contactView,
          filters: requestedFilters,
        });
        let data = await measureTaskStep("load tasks API all", () =>
          contactView
            ? fetchContactTasks({ force: options?.force })
            : fetchTasks({
                force: options?.force,
                filters: requestedFilters,
              })
        );
        if (
          startVersion !==
          getTasksCacheVersion({ contact: contactView, filters: requestedFilters })
        ) {
          startVersion = getTasksCacheVersion({
            contact: contactView,
            filters: requestedFilters,
          });
          data = await measureTaskStep("load tasks API retry all", () =>
            contactView
              ? fetchContactTasks({ force: true })
              : fetchTasks({
                  force: true,
                  filters: requestedFilters,
                })
          );
        }
        if (
          startVersion !==
          getTasksCacheVersion({ contact: contactView, filters: requestedFilters })
        ) {
          return (get() as VendState).tasks || [];
        }
        if (getTaskIdentityKey() !== loadTaskContextKey) {
          return (get() as VendState).tasks || [];
        }
        return data;
      },
      onSuccess: (data) => {
        measureTaskSync("load tasks merge all", () => {
          if ((get() as VendState).taskContextKey !== taskContextKey) return;
          const currentTasksById = new Map(
            ((get() as VendState).tasks || []).map((task) => [task.id, task])
          );
          set((state) => ({
            ...(state as VendState),
            tasks: data.map((task) =>
              mergeTaskSnapshot(currentTasksById.get(task.id), task)
            ),
            tasksFetchedAt: Date.now(),
            taskScopes: {
              ...((state as VendState).taskScopes || {}),
              [taskScopeKeys.all]: { loadedAt: Date.now() },
            },
          }));
        });
      },
    });
  },
  loadOperators: async () => {
    await runLoad({
      isLoading: () => (get() as VendState).operatorsLoading,
      setLoading: (loading) =>
        set((state) => ({
          ...(state as VendState),
          operatorsLoading: loading,
        })),
      setError: (message) =>
        set((state) => ({
          ...(state as VendState),
          operatorsError: message,
        })),
      setHydrated: (hydrated) =>
        set((state) => ({
          ...(state as VendState),
          operatorsHydrated: hydrated,
        })),
      errorMessage: "Failed to load operators",
      fetcher: fetchOperators,
      onSuccess: (data) =>
        set((state) => ({
          ...(state as VendState),
          operators: data,
        })),
    });
  },
  loadTask: async (taskId: string) => {
    const contextKey = getTaskIdentityKey();
    let snapshot = (get() as VendState).tasks.find((item) => item.id === taskId);
    let task = await fetchTaskById(taskId);
    if (contextKey !== getTaskIdentityKey()) return task;
    const current = (get() as VendState).tasks.find((item) => item.id === taskId);
    if (current !== snapshot) {
      // A save or another refresh finished during this read. Fetch current
      // details once instead of restoring the older response over that change.
      snapshot = current;
      task = await fetchTaskById(taskId);
      if (contextKey !== getTaskIdentityKey()) return task;
    }
    const byId = new Map(((get() as VendState).tasks || []).map((item) => [item.id, item]));
    const latest = byId.get(taskId);
    if (latest !== snapshot) return latest || task;
    const merged = mergeTaskSnapshot(byId.get(task.id), task);
    byId.set(task.id, merged);
    set({
      tasks: Array.from(byId.values()),
      tasksFetchedAt: Date.now(),
    } as VendState);
    return merged;
  },
  bulkUpdateTasks: async (taskIds, updates, options) => {
    const uniqueTaskIds = Array.from(new Set(taskIds.filter(Boolean)));
    if (!uniqueTaskIds.length) return [];
    const actionLabel = actionLabelForUpdate(updates as Task, uniqueTaskIds.length - 1);
    return await withWriteAction<Task[]>(
      actionLabel,
      async () => {
        const state = get() as VendState;
        const taskMap = new Map((state.tasks || []).map((task) => [task.id, task]));
        const resolvedTasks = await Promise.all(
          uniqueTaskIds.map(async (taskId) => {
            const existing = taskMap.get(taskId);
            if (existing) return existing;
            return state.loadTask(taskId);
          })
        );

        const payload: Record<string, unknown> = { ...updates };
        const statusUpdate = updates.status;
        if ("status" in payload) delete payload.status;
        if ("userId" in updates) {
          payload.assigneeId = updates.userId ?? null;
          delete payload.userId;
        }
        if ("includeRefundsInCommission" in updates) {
          payload.includeRefundsInCommission =
            updates.includeRefundsInCommission ?? null;
        }

        let updatedTasks = resolvedTasks;
        suppressTaskRealtime(uniqueTaskIds);
        if (Object.keys(payload).length > 0) {
          updatedTasks = await measureTaskStep(
            `${actionLabel} API bulk edit (${uniqueTaskIds.length})`,
            () => bulkEditTasksApi(uniqueTaskIds, {
              ...payload,
              status: statusUpdate,
            } as any)
          );
        } else if (statusUpdate !== undefined) {
          updatedTasks = await measureTaskStep(
            `${actionLabel} API bulk status (${uniqueTaskIds.length})`,
            () => updateTaskStatusesApi(uniqueTaskIds, statusUpdate)
          );
        }

        await finalizeTaskMutation({
          previousTasks: resolvedTasks,
          nextTasks: updatedTasks,
          skipRefresh: options?.skipRefresh,
        });
        return updatedTasks;
      },
      { suppressOverlay: options?.suppressOverlay }
    );
  },
  updateTask: async (taskId: string, updates, options) => {
    let current = ((get() as VendState).tasks || []).find(
      (t) => t.id === taskId
    );
    if (!current) {
      current = await (get() as VendState).loadTask(taskId);
    }
    if (!current) {
      throw new Error("Task not found");
    }
    const currentTask = current;
    const intentKeys = Object.keys(updates).filter(
      (key) => updates[key as keyof typeof updates] !== undefined,
    );
    const isTaskIntentOnly = intentKeys.every(
      (key) => key === "scheduledFor" || key === "userId" || key === "status",
    );
    if (
      isTaskIntentOnly &&
      intentKeys.length > 0 &&
      (!options?.skipStatusUpdate || !Object.prototype.hasOwnProperty.call(updates, "status"))
    ) {
      const updatedTasks = await (get() as VendState).bulkUpdateTasks(
        [taskId],
        updates,
        options,
      );
      return updatedTasks.find((task) => task.id === taskId) || updatedTasks[0] || currentTask;
    }
    const notesOnlyUpdate = isNotesOnlyUpdate(updates as Record<string, unknown>);
    const actionLabel = actionLabelForUpdate(updates as Task);
    return await withWriteAction<Task>(
      actionLabel,
      async () => {
        const connectedRealtimeSuppressTasks = notesOnlyUpdate
          ? getConnectedServiceBundleTasks(
              (get() as VendState).tasks || [],
              currentTask
            )
          : [];
        suppressTaskRealtime([
          taskId,
          ...connectedRealtimeSuppressTasks.map((task) => task.id),
        ]);
        const payload: Record<string, unknown> = { ...updates };
        const statusUpdate = updates.status;
        if ("status" in payload) {
          delete payload.status;
        }
        if ("userId" in updates) {
          payload.assigneeId = updates.userId ?? null;
          delete payload.userId;
        }
        if ("includeRefundsInCommission" in updates) {
          payload.includeRefundsInCommission = updates.includeRefundsInCommission ?? null;
        }
        if (statusUpdate === "cancelled") {
          if (currentTask.type === "machine_collection") {
            payload.gross = null;
            payload.refunds = null;
            payload.commission = null;
            payload.net = null;
          } else if (currentTask.type === "machine_refund") {
            payload.refunds = null;
          }
        }
        const shouldRefetchCollectionRevenue =
          currentTask.type === "machine_collection" &&
          ("grossCents" in payload ||
            "refundsCents" in payload ||
            "commissionCents" in payload ||
            "netCents" in payload ||
            "includeRefundsInCommission" in updates);
        const updated = (Object.keys(payload).length > 0
          ? await measureTaskStep(
              `${actionLabel} API edit`,
              () =>
                editTaskApi(
                  taskId,
                  currentTask.type,
                  payload as any,
                  shouldRefetchCollectionRevenue ? undefined : { currentTask }
                )
            )
          : currentTask) as Task;
        let statusUpdatedTask: Task = updated;
        if ("userId" in updates) {
          statusUpdatedTask.userId = updates.userId ?? null;
        }
        if (statusUpdate !== undefined && !options?.skipStatusUpdate) {
          const statusUpdatedTargets = await measureTaskStep(`${actionLabel} API status`, () =>
            updateTaskStatusesApi([taskId], statusUpdate)
          );
          statusUpdatedTask = statusUpdatedTargets[0] || applyStatusLocally(
            statusUpdatedTask,
            statusUpdate,
            completionTimestampForStatus(statusUpdate),
          );
          set({
            tasks: (get() as VendState).tasks.map((t) =>
              t.id === taskId ? statusUpdatedTask : t
            ),
          } as VendState);
        } else {
          set({
            tasks: (get() as VendState).tasks.map((t) =>
              t.id === taskId ? statusUpdatedTask : t
            ),
          } as VendState);
        }
        if (!options?.skipRefresh) {
          await finalizeTaskMutation({
            previousTasks: [currentTask],
            nextTasks: [
              statusUpdatedTask,
            ],
            skipScopeInvalidation: notesOnlyUpdate,
          });
        } else {
          if (!notesOnlyUpdate) {
            invalidateAffectedTaskScopes(
              [currentTask],
              [statusUpdatedTask]
            );
            await refreshMachineMetadataIfNeeded(
              [currentTask],
              [statusUpdatedTask]
            );
          }
        }
        return statusUpdatedTask;
      },
      { suppressOverlay: options?.suppressOverlay }
    );
  },
  updateTasksNotes: async (taskIds, notes, options) => {
    if (!taskIds.length) return;
    await withWriteAction(
      "Updating Notes",
      async () => {
        const state = get() as VendState;
        const taskMap = new Map(
          (state.tasks || []).map((task) => [task.id, task])
        );
        const resolvedTasks = await Promise.all(
          taskIds.map(async (id) => {
            const existing = taskMap.get(id);
            if (existing) return existing;
            return (state as VendState).loadTask(id);
          })
        );
        suppressTaskRealtime(taskIds);
        const updatedTasks = await measureTaskStep(
          `Updating Notes API bulk edit (${resolvedTasks.length})`,
          () =>
            bulkEditTasksApi(
              resolvedTasks.map((task) => task.id),
              { notes } as any
            )
        );
        await finalizeTaskMutation({
          previousTasks: resolvedTasks,
          nextTasks: updatedTasks,
          skipRefresh: options?.skipRefresh,
        });
      },
      { suppressOverlay: options?.suppressOverlay }
    );
  },
  updateTasksStatus: async (taskIds, nextStatus, options) => {
    if (!taskIds.length) return;
    const label = actionLabelForStatus(nextStatus, taskIds.length);
    await withWriteAction(
      label,
      async () => {
        const state = get() as VendState;
        suppressTaskRealtime(taskIds);
        const resolvedTasks = taskIds.map(
          (taskId) => state.tasks.find((task) => task.id === taskId) || null
        );
        const updatedTasks = await measureTaskStep(
          `${label} API bulk status (${taskIds.length})`,
          () =>
            updateTaskStatusesApi(taskIds, nextStatus, {
              cascadeBundle: options?.cascadeBundle,
            })
        );
        await finalizeTaskMutation({
          previousTasks: resolvedTasks,
          nextTasks: updatedTasks,
          skipRefresh: options?.skipRefresh,
        });
      }
    );
  },
  markTaskComplete: async (taskId: string) => {
    const state = get() as VendState;
    let current = (state.tasks || []).find((t) => t.id === taskId);
    if (!current) {
      current = await state.loadTask(taskId);
    }
    await state.updateTask(taskId, { status: "done" });
  },
  createTask: async (payload, options) => {
    return await withWriteAction(
      "Creating Task",
      async () => {
        suppressCreatedTaskRealtime();
        const seedTask = await measureTaskStep("Creating Task API create", () =>
          createTaskApi({
            ...payload,
          })
        );
        suppressTaskRealtime([seedTask.id]);
        await finalizeTaskMutation({
          previousTasks: [null],
          nextTasks: [seedTask],
          skipRefresh: options?.skipRefresh,
        });
        return seedTask;
      }
    );
  },
  createTasks: async (payloads, options) => {
    if (!payloads.length) return [];
    return await withWriteAction(
      payloads.length === 1 ? "Creating Task" : "Creating Tasks",
      async () => {
        suppressCreatedTaskRealtime();
        const seedTasks = await measureTaskStep(
          `Creating Tasks API bulk create (${payloads.length})`,
          () => bulkCreateTasksApi(payloads)
        );
        suppressTaskRealtime(seedTasks.map((task) => task.id));
        await finalizeTaskMutation({
          previousTasks: seedTasks.map(() => null),
          nextTasks: seedTasks,
          skipRefresh: options?.skipRefresh,
        });
        return seedTasks;
      }
    );
  },
  deleteTask: async (taskId: string) => {
    await withWriteAction("Deleting Task", async () => {
      const state = get() as VendState;
      const currentTasks = state.tasks || [];
      let current = currentTasks.find((task) => task.id === taskId);
      if (!current) {
        current = await state.loadTask(taskId);
      }
      const currentTask = current;
      if (!currentTask) {
        throw new Error("Failed to delete task");
      }
      suppressTaskRealtime([taskId]);
      await measureTaskStep("Deleting Task API delete", () =>
        deleteTaskApi(taskId, currentTask.type)
      );
      await finalizeTaskMutation({
        previousTasks: [currentTask],
        nextTasks: [null],
        deletedTaskIds: [taskId],
      });
    });
  },
  deleteTasks: async (taskIds: string[]) => {
    const uniqueTaskIds = Array.from(new Set(taskIds.filter(Boolean)));
    if (!uniqueTaskIds.length) return;
    await withWriteAction("Deleting Tasks", async () => {
      const state = get() as VendState;
      const currentTasks = state.tasks || [];
      const currentById = new Map(currentTasks.map((task) => [task.id, task]));
      const resolvedTasks = await Promise.all(
        uniqueTaskIds.map(async (taskId) => {
          const existing = currentById.get(taskId);
          if (existing) return existing;
          try {
            return await state.loadTask(taskId);
          } catch {
            return null;
          }
        })
      );
      suppressTaskRealtime(uniqueTaskIds);
      await measureTaskStep(
        `Deleting Tasks API bulk delete (${uniqueTaskIds.length})`,
        () => bulkDeleteTasksApi(uniqueTaskIds)
      );
      await finalizeTaskMutation({
        previousTasks: resolvedTasks,
        nextTasks: uniqueTaskIds.map(() => null),
        deletedTaskIds: uniqueTaskIds,
      });
    });
  },
  cancelTasks: async (taskIds, options) => {
    const uniqueTaskIds = Array.from(new Set(taskIds.filter(Boolean)));
    if (!uniqueTaskIds.length) return [];
    return await withWriteAction<Task[]>(
      actionLabelForStatus("cancelled", uniqueTaskIds.length),
      async () => {
        const state = get() as VendState;
        const taskMap = new Map((state.tasks || []).map((task) => [task.id, task]));
        const resolvedTasks = await Promise.all(
          uniqueTaskIds.map(async (taskId) => {
            const existing = taskMap.get(taskId);
            if (existing) return existing;
            return state.loadTask(taskId);
          })
        );
        suppressTaskRealtime(uniqueTaskIds);
        const updatedTasks = await measureTaskStep(
          `Cancelling Tasks API bulk cancel (${uniqueTaskIds.length})`,
          () => bulkCancelTasksApi(uniqueTaskIds)
        );
        await finalizeTaskMutation({
          previousTasks: resolvedTasks,
          nextTasks: updatedTasks,
          skipRefresh: options?.skipRefresh,
        });
        return updatedTasks;
      }
    );
  },
  setTaskRefillInventorySource: async (taskId, warehouseId, sourceMode, vanId) => {
    const state = get() as VendState;
    const previousTask =
      (state.tasks || []).find((task) => task.id === taskId) || null;
    const updatedTask = await withWriteAction("Updating Task", () =>
      measureTaskStep("Updating Task API refill source", () =>
        setRefillInventorySourceApi(taskId, warehouseId, sourceMode,
          sourceMode === "van" ? vanId ?? previousTask?.inventorySourceVanId ?? null : null)
      )
    );
    await finalizeTaskMutation({
      previousTasks: [previousTask],
      nextTasks: [updatedTask],
      skipRefresh: true,
    });
    return updatedTask;
  },
  createTaskRefillInventoryPickup: async (taskId, warehouseId, vanId) => {
    const identity = getTaskIdentityKey();
    const previousTask = (get() as VendState).tasks.find((task) => task.id === taskId);
    return withWriteAction("Creating Warehouse Pickup", async () => {
      const pickup = await createRefillInventoryPickup(taskId, warehouseId, vanId);
      if (identity !== getTaskIdentityKey()) throw new Error("Account changed. Reload tasks.");
      const refill = await fetchTaskById(taskId);
      if (identity !== getTaskIdentityKey()) throw new Error("Account changed. Reload tasks.");
      await finalizeTaskMutation({ previousTasks: [previousTask], nextTasks: [refill, pickup], skipRefresh: true });
      return refill;
    });
  },
  completeTaskRefillInventory: async (taskId, items) => {
    const state = get() as VendState;
    const previousTask =
      (state.tasks || []).find((task) => task.id === taskId) || null;
    const updatedTask = await withWriteAction("Completing Task", () =>
      measureTaskStep("Completing Task API refill inventory", () =>
        completeRefillInventoryTaskApi(taskId, items)
      )
    );
    await finalizeTaskMutation({
      previousTasks: [previousTask],
      nextTasks: [updatedTask],
      skipRefresh: true,
    });
    return updatedTask;
  },
  deleteTaskAsset: async (assetId, taskId) => {
    await withWriteAction("Deleting Photo", async () => {
      await measureTaskStep("Deleting Photo API task asset", () =>
        deleteTaskAssetApi(assetId)
      );
      if (taskId) {
        const updatedTask = await (get() as VendState).loadTask(taskId);
        await finalizeTaskMutation({
          previousTasks: [null],
          nextTasks: [updatedTask],
          skipRefresh: true,
        });
      }
    });
  },
  backfillCollectionTasks: async (payload) => {
    const createdTasks = await withWriteAction("Creating Tasks", () =>
      measureTaskStep("Creating Tasks API collection backfill", () =>
        backfillCollectionTasksApi(payload)
      )
    );
    await finalizeTaskMutation({
      previousTasks: createdTasks.map(() => null),
      nextTasks: createdTasks,
      skipRefresh: true,
    });
    return createdTasks;
  },
  };
};
